import type { RouteRecordRaw } from "vue-router";
import type { RouteAccess } from "../session/access.ts";

declare module "vue-router" {
  interface RouteMeta {
    /** who may open the route; the guard decides with it (src/session/access.ts) */
    access: RouteAccess;
    /** page and navigation title, German like every customer-facing text */
    title: string;
    /**
     * the navigation group the route appears in (src/router/navigation.ts): the overview and the
     * products, the customer's account, the staff area; absent for routes outside the navigation
     */
    nav?: "products" | "account" | "staff";
    /** the navigation item a page outside the navigation keeps current, as an agent's page its family's */
    navItem?: string;
    /** shows the login dialog over the page; `/login` is where the callback sends errors (08 §1) */
    loginDialog?: boolean;
  }
}

const HomePage = () => import("../pages/HomePage.vue");
const CatalogPage = () => import("../pages/CatalogPage.vue");
const ProductPage = () => import("../pages/ProductPage.vue");
const AiCockpitPage = () => import("../pages/AiCockpitPage.vue");
const FeratelAgentsPage = () => import("../pages/FeratelAgentsPage.vue");
const DataPixiesPage = () => import("../pages/DataPixiesPage.vue");
const FeratelAgentPage = () => import("../pages/FeratelAgentPage.vue");
const LoginPage = () => import("../pages/LoginPage.vue");
const ApplicationPage = () => import("../pages/ApplicationPage.vue");
const OrdersPage = () => import("../pages/OrdersPage.vue");
const EntitlementsPage = () => import("../pages/EntitlementsPage.vue");
const UsagePage = () => import("../pages/UsagePage.vue");
const StaffCustomersPage = () => import("../pages/StaffCustomersPage.vue");
const StaffCustomerPage = () => import("../pages/StaffCustomerPage.vue");
const StaffOrdersPage = () => import("../pages/StaffOrdersPage.vue");
const StaffOrderPage = () => import("../pages/StaffOrderPage.vue");
const StaffProductsPage = () => import("../pages/StaffProductsPage.vue");
const StaffTasksPage = () => import("../pages/StaffTasksPage.vue");
const StaffReportsPage = () => import("../pages/StaffReportsPage.vue");
const StaffInquiriesPage = () => import("../pages/StaffInquiriesPage.vue");
const StaffInquiryPage = () => import("../pages/StaffInquiryPage.vue");
const UsersPage = () => import("../pages/UsersPage.vue");
const NotFoundPage = () => import("../pages/NotFoundPage.vue");

/**
 * The route table (07 §5, ADR-066). The areas of 07 §5 by role, each behind the permission the
 * session lists for it (ADR-074). The order of the entries is the order of the navigation, as in
 * the design prototype (`renderNav`, ADR-150, point 1).
 */
export const portalRoutes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "home",
    component: HomePage,
    meta: { access: { kind: "customer", permission: "customer.view" }, title: "Übersicht", nav: "products" },
  },
  {
    // the overview of all products for guests and staff; a customer's "Übersicht" is `home`, the
    // address stays for links and bookmarks (ADR-150, point 3)
    path: "/catalog",
    name: "catalog",
    component: CatalogPage,
    meta: { access: { kind: "catalog" }, title: "Übersicht", nav: "products" },
  },
  // The families' pages under /catalog/<family>, a product's under /catalog/<family>/<slug> (ADR-150
  // point 4, addendum of 2026-10-29); static segments rank before /catalog/:slug.
  {
    // the aiCockpit's own page, with its ordering in v1; there is no product level below it
    path: "/catalog/ai-cockpit",
    name: "aiCockpit",
    component: AiCockpitPage,
    meta: { access: { kind: "catalog" }, title: "aiCockpit", nav: "products" },
  },
  {
    path: "/catalog/feratel-agents",
    name: "feratelAgents",
    component: FeratelAgentsPage,
    meta: { access: { kind: "catalog" }, title: "feratel Agenten", nav: "products" },
  },
  {
    // an agent's detail page as the design's `agentPageHtml`; the menu keeps "feratel Agenten" current
    path: "/catalog/feratel-agents/:slug",
    name: "feratelAgent",
    component: FeratelAgentPage,
    meta: { access: { kind: "catalog" }, title: "feratel Agent", navItem: "feratelAgents" },
  },
  {
    // the bundles have no detail pages yet; they will, under /catalog/datapixies/<slug> (TL)
    path: "/catalog/datapixies",
    name: "dataPixies",
    component: DataPixiesPage,
    meta: { access: { kind: "catalog" }, title: "dataPixies", nav: "products" },
  },
  // the addresses of parts 2 and 3 of ADR-150, pushed on 2026-10-29
  { path: "/ai-cockpit", redirect: { name: "aiCockpit" } },
  { path: "/feratel-agents", redirect: { name: "feratelAgents" } },
  { path: "/datapixies", redirect: { name: "dataPixies" } },
  {
    // a product without a page of its family; a feratel agent is led on to its detail page
    path: "/catalog/:slug",
    name: "product",
    component: ProductPage,
    meta: { access: { kind: "catalog" }, title: "Produkt" },
  },
  {
    path: "/login",
    name: "login",
    component: LoginPage,
    meta: { access: { kind: "public" }, title: "Anmelden", loginDialog: true },
  },
  {
    // every "Für die Beta bewerben" leads here, also from the login with the guest view off
    // (ADR-143 addendum 3, TL 2026-10-28); `?lead=instance` from a login refused for its instance
    path: "/beta",
    name: "application",
    component: ApplicationPage,
    meta: { access: { kind: "public" }, title: "Für die Beta bewerben" },
  },
  {
    path: "/entitlements",
    name: "entitlements",
    component: EntitlementsPage,
    meta: {
      access: { kind: "customer", permission: "entitlement.view" },
      title: "Berechtigungen",
      nav: "account",
    },
  },
  {
    path: "/orders",
    name: "orders",
    component: OrdersPage,
    meta: {
      access: { kind: "customer", permission: "order.view" },
      title: "Bestellungen",
      nav: "account",
    },
  },
  {
    path: "/usage",
    name: "usage",
    component: UsagePage,
    meta: {
      access: { kind: "customer", permission: "usage.view" },
      title: "Verbrauch",
      nav: "account",
    },
  },
  {
    path: "/users",
    name: "users",
    component: UsersPage,
    meta: {
      access: { kind: "customer", permission: "user.view" },
      title: "Benutzer",
      nav: "account",
    },
  },
  {
    // the list beside the file (C-18): the file is the child, the list opens the first customer
    path: "/staff/customers",
    name: "staffCustomers",
    component: StaffCustomersPage,
    meta: {
      access: { kind: "staff", permission: "customer.view" },
      title: "Kunden",
      nav: "staff",
    },
    children: [
      {
        path: ":id",
        name: "staffCustomer",
        component: StaffCustomerPage,
        meta: { access: { kind: "staff", permission: "customer.view" }, title: "Kunde" },
      },
    ],
  },
  {
    path: "/staff/products",
    name: "staffProducts",
    component: StaffProductsPage,
    meta: {
      // support and admin read the terms with product.view, the admin sets them (UC-26, ADR-125
      // Nachtrag 2026-10-19): `actions["product.configure"]` decides the edit
      access: { kind: "staff", permission: "product.view" },
      title: "Konditionen",
      nav: "staff",
    },
  },
  {
    // one order on its own page, the mails' direct link (ADR-135 Nachtrag): a child, so the
    // navigation stays on the orders; the list shows it instead of itself
    path: "/staff/orders",
    name: "staffOrders",
    component: StaffOrdersPage,
    meta: {
      access: { kind: "staff", permission: "order.view" },
      title: "Bestellungen",
      nav: "staff",
    },
    children: [
      {
        path: ":id",
        name: "staffOrder",
        component: StaffOrderPage,
        meta: { access: { kind: "staff", permission: "order.view" }, title: "Bestellung" },
      },
    ],
  },
  {
    path: "/staff/tasks",
    name: "staffTasks",
    component: StaffTasksPage,
    meta: {
      access: { kind: "staff", permission: "task.complete" },
      title: "Aufgaben",
      nav: "staff",
    },
  },
  {
    // one inquiry on its own page, the mails' direct link (ADR-135 Nachtrag), as the orders
    path: "/staff/inquiries",
    name: "staffInquiries",
    component: StaffInquiriesPage,
    meta: {
      access: { kind: "staff", permission: "inquiry.process" },
      title: "Anfragen",
      nav: "staff",
    },
    children: [
      {
        path: ":id",
        name: "staffInquiry",
        component: StaffInquiryPage,
        meta: { access: { kind: "staff", permission: "inquiry.process" }, title: "Anfrage" },
      },
    ],
  },
  {
    path: "/staff/reports",
    name: "staffReports",
    component: StaffReportsPage,
    meta: {
      access: { kind: "staff", permission: "usage.view" },
      title: "Monatsberichte",
      nav: "staff",
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "notFound",
    component: NotFoundPage,
    meta: { access: { kind: "public" }, title: "Seite nicht gefunden" },
  },
];
